import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Lock
} from '@mui/icons-material';
import logoImage from '../assets/images/TrueDotDESIGN.png';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginData } from '../types';

// Validation schema
const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<LoginData>({
    resolver: yupResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginData) => {
    try {
      setIsLoading(true);
      setLoginError('');
      setHasError(false);
      
      await login(data);
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed. Please check your credentials and try again.';
      setLoginError(errorMessage);
      setHasError(true);
      setValue('password', ''); // Clear password on failed login
      
      // Focus back to email or password field based on error type
      setTimeout(() => {
        if (errorMessage.includes('No account found')) {
          // Focus email field for user not found
          emailRef.current?.focus();
        } else if (errorMessage.includes('Incorrect password')) {
          // Focus password field for wrong password
          passwordRef.current?.focus();
        }
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset error shake animation after it plays
  useEffect(() => {
    if (hasError) {
      const timer = setTimeout(() => setHasError(false), 600);
      return () => clearTimeout(timer);
    }
  }, [hasError]);

  // Load custom logo for login page
  useEffect(() => {
    const loadLoginLogo = async () => {
      try {
        const response = await fetch('/api/auth/login-logo');
        const data = await response.json();
        if (data.success && data.data.logoUrl) {
          setCustomLogo(data.data.logoUrl);
        }
      } catch (error) {
        console.error('Error loading login logo:', error);
        // Use default logo on error
      }
    };
    
    loadLoginLogo();
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card 
          elevation={24} 
          sx={{ 
            borderRadius: 4,
            position: 'relative',
            zIndex: 1,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: hasError ? '2px solid #d32f2f' : '1px solid rgba(255, 255, 255, 0.2)',
            animation: hasError ? 'shake 0.6s ease-in-out' : 'none',
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-8px)' },
              '20%, 40%, 60%, 80%': { transform: 'translateX(8px)' },
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 5,
              }}
            >
              <Box
                sx={{
                  width: 200,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: 2,
                  boxShadow: '0 4px 16px rgba(255, 94, 91, 0.15)',
                }}
              >
                <img 
                  src={customLogo || logoImage} 
                  alt="Company Logo" 
                  style={{
                    height: '60px',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Typography 
                variant="body1" 
                textAlign="center"
                sx={{ 
                  maxWidth: 320,
                  lineHeight: 1.6,
                  fontWeight: 400,
                  color: 'rgba(45, 45, 45, 0.8)',
                  textShadow: '0 1px 2px rgba(255, 255, 255, 0.3)',
                }}
              >
                Welcome back. Sign in to access your dashboard.
              </Typography>
            </Box>

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
              {loginError && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    fontWeight: 500,
                    '& .MuiAlert-message': {
                      fontSize: '0.95rem',
                      fontWeight: 500,
                    },
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    border: '2px solid #f44336',
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      color: '#f44336',
                    },
                    animation: hasError ? 'pulse 0.3s ease-in-out' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.02)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  }}
                >
                  {loginError}
                </Alert>
              )}

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={emailRef}
                    fullWidth
                    type="email"
                    label="Email"
                    placeholder="Enter your email address"
                    error={!!errors.email || hasError}
                    helperText={errors.email?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                    autoComplete="email"
                    autoFocus
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={passwordRef}
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    placeholder="Enter your password"
                    error={!!errors.password || hasError}
                    helperText={errors.password?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                    autoComplete="current-password"
                  />
                )}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  mb: 4,
                  py: 1.8,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #FF8C00 0%, #FF7A00 50%, #FF6500 100%)',
                  boxShadow: '0 4px 20px rgba(255, 140, 0, 0.4)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E67D00 0%, #E66E00 50%, #E65B00 100%)',
                    boxShadow: '0 6px 25px rgba(255, 140, 0, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #CCCCCC 0%, #999999 100%)',
                    boxShadow: 'none',
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Signing In...
                  </Box>
                ) : 'Sign In'}
              </Button>

            </Box>

          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LoginPage; 